/**
 * Agent operations for CDP SDK.
 *
 * Wraps the apboa AI-agent service endpoints (`/apboa/api/**`), which are
 * served from the same origin as CDP but authenticated with an agent SK
 * (issued by `/api/cdp/agent/account/getsk`) instead of the CDP Bearer token.
 */

import { CDPSession, getSession } from '../session';
import type { RequestConfig } from '../types';

const AGENT_BASE = '/apboa/api/agent';
const MODEL_BASE = '/apboa/api/model';
const CHAT_SESSION_BASE = '/apboa/api/agent/chat/session';

export class AgentManager {
  constructor(private readonly session: CDPSession = getSession()) {}
  private sk: string | null = null;

  setSecurityKey(sk: string | null) {
    this.sk = sk;
  }

  async getSecurityKey(): Promise<string> {
    const res = await this.session.post<Record<string, any>>('/api/cdp/agent/account/getsk');
    this.sk = (res?.data ?? null) as string | null;
    if (!this.sk) {
      throw new Error('Failed to obtain agent security key.');
    }
    return this.sk;
  }

  private async agentRequest<T = any>(method: string, endpoint: string, config?: RequestConfig): Promise<T> {
    if (!this.sk) {
      await this.getSecurityKey();
    }
    return this.session.request<T>(method, endpoint, {
      ...config,
      headers: { ...(config?.headers ?? {}), Authorization: this.sk as string },
    });
  }

  async pageAgents(query?: Record<string, any>): Promise<Record<string, any>> {
    return this.agentRequest('GET', `${AGENT_BASE}/definition/page`, { params: query });
  }

  async getAgent(id: number | string): Promise<Record<string, any>> {
    return this.agentRequest('GET', `${AGENT_BASE}/definition/${id}`);
  }

  async createAgent(vo: Record<string, any>): Promise<Record<string, any>> {
    return this.agentRequest('POST', `${AGENT_BASE}/definition`, { data: vo });
  }

  async updateAgent(vo: Record<string, any>): Promise<Record<string, any>> {
    return this.agentRequest('PUT', `${AGENT_BASE}/definition`, { data: vo });
  }

  async deleteAgents(ids: (number | string)[]): Promise<Record<string, any>> {
    return this.agentRequest('DELETE', `${AGENT_BASE}/definition`, { data: ids });
  }

  async usedWithAgent(ids: (number | string)[]): Promise<Record<string, any>> {
    return this.agentRequest('POST', `${AGENT_BASE}/definition/used-with-agent`, { data: ids });
  }

  async listTags(): Promise<Record<string, any>> {
    return this.agentRequest('GET', `${AGENT_BASE}/definition/get/tags`);
  }

  async allowFileType(id: number | string): Promise<Record<string, any>> {
    return this.agentRequest('GET', `${AGENT_BASE}/definition/${id}/allow/file-type`);
  }

  async agentDoTool(toolName: string, args?: Record<string, any>): Promise<Record<string, any>> {
    return this.agentRequest('POST', `${AGENT_BASE}/endpoint/do/${toolName}/tool`, { data: args });
  }

  async chatStream(
    agentCode: string,
    payload: Record<string, any>,
    onEvent?: (event: any) => void,
    onError?: (error: any) => void,
  ): Promise<AbortController> {
    if (!this.sk) {
      await this.getSecurityKey();
    }
    const baseUrl = this.session.baseUrl ?? '';
    const controller = new AbortController();

    fetch(`${baseUrl}/apboa/api/runtime/agui/run/${agentCode}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'text/event-stream',
        Authorization: this.sk as string,
      },
      body: JSON.stringify(payload),
      signal: controller.signal,
    }).then(async (response) => {
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const reader = response.body!.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() as string;

        for (const line of lines) {
          if (line.startsWith('data:')) {
            const data = line.replace(/^data:\s*/, '').trim();
            if (!data) continue;
            try {
              let parsed = JSON.parse(data);
              if (typeof parsed === 'string') {
                parsed = JSON.parse(parsed.trim());
              }
              if (onEvent) {
                onEvent(parsed);
              }
            } catch (e) {
              console.warn('SSE parse error:', e);
            }
          }
        }
      }
    }).catch((error) => {
      if (error.name === 'AbortError') return;
      if (onError) onError(error);
    });

    return controller;
  }

  async createSession(dto: Record<string, any>): Promise<Record<string, any>> {
    return this.agentRequest('POST', CHAT_SESSION_BASE, { data: dto });
  }

  async appendMessage(sessionId: number | string, dto: Record<string, any>): Promise<Record<string, any>> {
    return this.agentRequest('POST', `${CHAT_SESSION_BASE}/${sessionId}/message`, { data: dto });
  }

  async regenerateMessage(sessionId: number | string, dto: Record<string, any>): Promise<Record<string, any>> {
    return this.agentRequest('POST', `${CHAT_SESSION_BASE}/${sessionId}/regenerate`, { data: dto });
  }

  async switchCurrentMessage(sessionId: number | string, messageId: number | string): Promise<Record<string, any>> {
    return this.agentRequest('PUT', `${CHAT_SESSION_BASE}/${sessionId}/current`, { params: { messageId } });
  }

  async getCurrentMessages(sessionId: number | string): Promise<Record<string, any>> {
    return this.agentRequest('GET', `${CHAT_SESSION_BASE}/${sessionId}/messages/current`);
  }

  async getCurrentMessagesPaged(sessionId: number | string, params?: Record<string, any>): Promise<Record<string, any>> {
    return this.agentRequest('GET', `${CHAT_SESSION_BASE}/${sessionId}/messages/paged`, { params });
  }

  async listSessions(query?: Record<string, any>): Promise<Record<string, any>> {
    return this.agentRequest('GET', `${CHAT_SESSION_BASE}/list`, { params: query });
  }

  async pageSessions(query?: Record<string, any>): Promise<Record<string, any>> {
    return this.agentRequest('GET', `${CHAT_SESSION_BASE}/page`, { params: query });
  }

  async getSessionDetail(id: number | string): Promise<Record<string, any>> {
    return this.agentRequest('GET', `${CHAT_SESSION_BASE}/${id}`);
  }

  async pinSession(id: number | string): Promise<Record<string, any>> {
    return this.agentRequest('PUT', `${CHAT_SESSION_BASE}/${id}/pin`);
  }

  async unpinSession(id: number | string): Promise<Record<string, any>> {
    return this.agentRequest('PUT', `${CHAT_SESSION_BASE}/${id}/unpin`);
  }

  async updateSessionTitle(id: number | string, title: string): Promise<Record<string, any>> {
    return this.agentRequest('PUT', `${CHAT_SESSION_BASE}/${id}/title`, { params: { title } });
  }

  async deleteSession(id: number | string): Promise<Record<string, any>> {
    return this.agentRequest('DELETE', `${CHAT_SESSION_BASE}/${id}`);
  }

  async configPage(query?: Record<string, any>): Promise<Record<string, any>> {
    return this.agentRequest('GET', `${MODEL_BASE}/config/page`, { params: query });
  }

  async configDetail(id: number | string): Promise<Record<string, any>> {
    return this.agentRequest('GET', `${MODEL_BASE}/config/${id}`);
  }

  async configSave(entity: Record<string, any>): Promise<Record<string, any>> {
    return this.agentRequest('POST', `${MODEL_BASE}/config`, { data: entity });
  }

  async configUpdate(entity: Record<string, any>): Promise<Record<string, any>> {
    return this.agentRequest('PUT', `${MODEL_BASE}/config`, { data: entity });
  }

  async configRemove(ids: (number | string)[]): Promise<Record<string, any>> {
    return this.agentRequest('DELETE', `${MODEL_BASE}/config`, { data: ids });
  }

  async configUsedWithAgent(ids: (number | string)[]): Promise<Record<string, any>> {
    return this.agentRequest('POST', `${MODEL_BASE}/config/used-with-agent`, { data: ids });
  }

  async providerPage(query?: Record<string, any>): Promise<Record<string, any>> {
    return this.agentRequest('GET', `${MODEL_BASE}/provider/page`, { params: query });
  }

  async providerDetail(id: number | string): Promise<Record<string, any>> {
    return this.agentRequest('GET', `${MODEL_BASE}/provider/${id}`);
  }

  async providerSave(entity: Record<string, any>): Promise<Record<string, any>> {
    return this.agentRequest('POST', `${MODEL_BASE}/provider`, { data: entity });
  }

  async providerUpdate(entity: Record<string, any>): Promise<Record<string, any>> {
    return this.agentRequest('PUT', `${MODEL_BASE}/provider`, { data: entity });
  }

  async providerRemove(ids: (number | string)[]): Promise<Record<string, any>> {
    return this.agentRequest('DELETE', `${MODEL_BASE}/provider`, { data: ids });
  }

  async providerUsedWithModel(ids: (number | string)[]): Promise<Record<string, any>> {
    return this.agentRequest('POST', `${MODEL_BASE}/provider/used-with-model`, { data: ids });
  }
}
